import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import SendTestPushNotificationModal from '@/components/admin/pushNotification/SendTestPushNotificationModal';
import FullscreenError from '@/components/utils/FullscreenError';
import { faBell, faSpinner, faArrowLeft, faPlus, faEye, faExclamationTriangle, faCheckCircle, faClock } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, shortObjectID } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { PushNotification } from '@/models';
import { PushNotificationStatus } from '@/models';

const pageSize = 50;

export default function AdminPushNotificationsList() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<PushNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<PushNotificationStatus | 'ALL'>('ALL');
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [currentPage, statusFilter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listPushNotifications({
        limit: pageSize,
        next: cursorToken,
        status: statusFilter === 'ALL' ? undefined : statusFilter
      });
      setNotifications(response.notifications);
      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading push notifications list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Push-Benachrichtigungen');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newStatus: PushNotificationStatus | 'ALL') => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const getStatusIcon = (status: PushNotificationStatus) => {
    switch (status) {
      case 'WAITING':
        return <FontAwesomeIcon icon={faClock} className="w-4 h-4 text-yellow-600" />;
      case 'SUCCESS':
        return <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-600" />;
      case 'FAILED':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: PushNotificationStatus) => {
    switch (status) {
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'SUCCESS':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'FAILED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Push-Benachrichtigungen nicht verfügbar"
        message={error || 'Die Push-Benachrichtigungen-Liste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchNotifications}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin"
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Push-Benachrichtigungen ({total})
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsTestModalOpen(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer"
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
            <span>Test-Benachrichtigung senden</span>
          </button>
        </div>
      </div>

      {/* Filter section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-4 self-center">Status:</span>
          {(['ALL', PushNotificationStatus.WAITING, PushNotificationStatus.SUCCESS, PushNotificationStatus.FAILED] as const).map((status) => (
            <button
              key={status}
              onClick={() => handleFilterChange(status)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                statusFilter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {status === 'ALL' ? 'Alle' : status}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {notifications.length > 0 && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Benutzer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Titel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Versuche
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Benachrichtigungs-ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                        {getStatusIcon(notification.status)}
                        <span className="ml-1">{notification.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/admin/users/${notification.userID}`}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline font-mono"
                      >
                        {shortObjectID(notification.userID)}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {notification.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {notification.tryCount} / {notification.maxTries}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {shortObjectID(notification.id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(notification.created)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <Link
                        to={`/admin/push-notifications/${notification.id}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center"
                      >
                        <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-1" />
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}

      {notifications.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faBell} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Keine Push-Benachrichtigungen gefunden</p>
        </div>
      )}

      <SendTestPushNotificationModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        onSent={fetchNotifications}
      />
    </>
  );
}
